import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader.js";
import { RGBELoader } from "three/examples/jsm/loaders/RGBELoader.js";
import type {
    AssetConfig,
    AssetType,
    LoadedAsset,
    CacheEntry,
    AssetManagerConfig,
    IAssetManager,
    TextureConfig,
    ModelConfig,
    AudioConfig,
    HDRIConfig,
    SpritesheetConfig,
} from "./types";

/**
 * Core Asset Manager for loading and caching Three.js assets.
 *
 * **Public API (use these from other modules):**
 * - **loadAsset(config)** – Load one asset. Use when you need the asset or want errors thrown.
 * - **loadAssets(configs)** – Load many assets in parallel. Use when loading multiple at once.
 * - **tryLoadAsset(config)** – Load one asset, returns null on failure. Use when you prefer no throw.
 * - **getAsset(url)** – Get a previously loaded asset from cache. Use when asset was already loaded.
 * - **hasAsset(url)** – Check if an asset is in cache.
 * - **clearAsset(url)** / **clearCache()** – Remove one or all cached assets.
 * - **getCacheStats()** – Get cache size and hit rate (e.g. for debug UI).
 * - **dispose()** – Release all resources.
 *
 * All other methods are private implementation details.
 *
 * @example
 * ```typescript
 * const assetManager = new AssetManager();
 * const result = await assetManager.loadAsset({
 *   url: 'textures/sand_diffuse.jpg',
 *   type: 'texture'
 * });
 * const assets = await assetManager.loadAssets([
 *   { url: 'textures/sand_diffuse.jpg', type: 'texture' },
 *   { url: 'models/character.glb', type: 'model' }
 * ]);
 * ```
 */
export class AssetManager implements IAssetManager {
    private cache = new Map<string, CacheEntry>();
    private loaders = new Map<AssetType, any>();
    private config: AssetManagerConfig;
    private loadingPromises = new Map<string, Promise<LoadedAsset>>();
    private cacheHits = 0;
    private cacheMisses = 0;

    constructor(config: AssetManagerConfig = {}) {
        this.config = {
            baseUrl: "",
            maxCacheSize: 100, // 100MB
            enableCompression: true,
            loadingTimeout: 30000, // 30 seconds
            dracoDecoderPath: "https://www.gstatic.com/draco/versioned/decoders/1.5.7/", // Default Draco decoder CDN path
            defaultTextureConfig: {
                mapping: THREE.UVMapping,
                wrapS: THREE.RepeatWrapping,
                wrapT: THREE.RepeatWrapping,
                magFilter: THREE.LinearFilter,
                minFilter: THREE.LinearMipmapLinearFilter,
                generateMipmaps: true,
                anisotropy: 4,
            },
            defaultModelConfig: {
                scale: 1,
                castShadow: true,
                receiveShadow: true,
            },
            defaultAudioConfig: {
                volume: 1,
                loop: false,
                autoplay: false,
            },
            defaultHDRIConfig: {
                intensity: 1,
                generateEnvironmentMap: true,
                environmentMapResolution: 512,
            },
            ...config,
        };

        this.initializeLoaders();
    }

    /**
     * Initialize Three.js loaders for different asset types
     * @private
     */
    private initializeLoaders(): void {
        // Texture loader (JPG, PNG)
        this.loaders.set("texture", new THREE.TextureLoader());

        // Model loader (GLB, GLTF) with Draco compression support
        const gltfLoader = new GLTFLoader();
        const dracoLoader = new DRACOLoader();
        dracoLoader.setDecoderPath(this.config.dracoDecoderPath || "https://www.gstatic.com/draco/versioned/decoders/1.5.7/");
        gltfLoader.setDRACOLoader(dracoLoader);
        this.loaders.set("model", gltfLoader);

        // HDRI loader (HDR, EXR)
        this.loaders.set("hdri", new RGBELoader());

        // Audio loader (using Web Audio API)
        this.loaders.set("audio", null); // Will use fetch + AudioContext
    }

    /**
     * Load a single asset. Use this when you need one asset and want errors thrown on failure.
     * @param config Asset configuration (url, type, and optional type-specific options)
     * @returns Promise resolving to loaded asset with metadata
     */
    async loadAsset<T = any>(config: AssetConfig): Promise<LoadedAsset<T>> {
        const startTime = performance.now();
        const fullUrl = this.getFullUrl(config.url);

        // Check cache first
        if (this.cache.has(fullUrl)) {
            this.cacheHits++;
            const entry = this.cache.get(fullUrl)!;
            entry.accessCount++;
            entry.lastAccess = Date.now();

            return {
                asset: entry.asset,
                type: entry.type,
                url: fullUrl,
                loadTime: 0,
                size: entry.size,
                timestamp: entry.timestamp,
            };
        }

        // Check if already loading
        if (this.loadingPromises.has(fullUrl)) {
            return this.loadingPromises.get(fullUrl)!;
        }

        // Start loading
        this.cacheMisses++;
        const loadingPromise = this.loadAssetInternal<T>(
            config,
            fullUrl,
            startTime
        );
        this.loadingPromises.set(fullUrl, loadingPromise);

        try {
            const result = await loadingPromise;
            this.loadingPromises.delete(fullUrl);
            return result;
        } catch (error) {
            this.loadingPromises.delete(fullUrl);
            throw error;
        }
    }

    /**
     * Load multiple assets in parallel. Use this when you need several assets at once.
     * @param configs Array of asset configurations
     * @returns Promise resolving to array of loaded assets (same order as configs)
     */
    async loadAssets<T = any>(configs: AssetConfig[]): Promise<LoadedAsset<T>[]> {
        const promises = configs.map((config) => this.loadAsset<T>(config));
        return Promise.all(promises);
    }

    /**
     * Load a single asset; returns the raw asset or null on failure (does not throw).
     * Use this when you prefer handling failure with null instead of try/catch.
     * @param config Asset configuration
     * @returns Promise resolving to the asset, or null if loading fails
     */
    async tryLoadAsset<T = any>(config: AssetConfig): Promise<T | null> {
        try {
            const result = await this.loadAsset<T>(config);
            return result.asset;
        } catch (error) {
            console.warn('Failed to load asset:', error);
            return null;
        }
    }

    /**
     * Get a previously loaded asset from cache. Use when the asset was already loaded (e.g. by loadAsset).
     * @param url Asset URL (same as used when loading)
     * @returns Cached asset or null if not in cache
     */
    getAsset<T = any>(url: string): T | null {
        const fullUrl = this.getFullUrl(url);
        const entry = this.cache.get(fullUrl);

        if (entry) {
            entry.accessCount++;
            entry.lastAccess = Date.now();
            return entry.asset as T;
        }

        return null;
    }

    /**
     * Check if an asset is in cache. Use before getAsset to avoid null.
     * @param url Asset URL
     * @returns True if asset is cached
     */
    hasAsset(url: string): boolean {
        const fullUrl = this.getFullUrl(url);
        return this.cache.has(fullUrl);
    }

    /**
     * Remove one asset from cache and dispose it. Use to free memory for a single URL.
     * @param url Asset URL
     * @returns True if asset was in cache and was cleared
     */
    clearAsset(url: string): boolean {
        const fullUrl = this.getFullUrl(url);
        const entry = this.cache.get(fullUrl);

        if (entry) {
            this.disposeAsset(entry.asset, entry.type);
            this.cache.delete(fullUrl);
            return true;
        }

        return false;
    }

    /**
     * Remove all assets from cache and dispose them. Use when resetting or freeing memory.
     */
    clearCache(): void {
        for (const [url, entry] of this.cache) {
            console.log(`Clearing cached asset: ${url}`);
            this.disposeAsset(entry.asset, entry.type);
        }
        this.cache.clear();
        this.cacheHits = 0;
        this.cacheMisses = 0;
    }

    /**
     * Get cache statistics (total assets, size, hit rate). Use for debug UI or monitoring.
     * @returns { totalAssets, totalSize (bytes), hitRate (0–100) }
     */
    getCacheStats(): { totalAssets: number; totalSize: number; hitRate: number } {
        let totalSize = 0;
        for (const entry of this.cache.values()) {
            totalSize += entry.size;
        }

        const totalRequests = this.cacheHits + this.cacheMisses;
        const hitRate =
            totalRequests > 0 ? (this.cacheHits / totalRequests) * 100 : 0;

        return {
            totalAssets: this.cache.size,
            totalSize,
            hitRate,
        };
    }

    /**
     * Release all cached assets and clear the manager. Use when destroying the scene or switching levels.
     */
    dispose(): void {
        this.clearCache();
        this.loadingPromises.clear();
    }

    /**
     * Internal asset loading implementation
     * @private
     */
    private async loadAssetInternal<T>(
        config: AssetConfig,
        fullUrl: string,
        startTime: number
    ): Promise<LoadedAsset<T>> {
        const assetType = this.detectAssetType(config);
        let asset: T;
        let size = 0;

        try {
            switch (assetType) {
                case "texture":
                    asset = (await this.loadTexture(fullUrl, config.texture)) as T;
                    break;
                case "model":
                    asset = (await this.loadModel(fullUrl, config.model)) as T;
                    break;
                case "hdri":
                    asset = (await this.loadHDRI(fullUrl, config.hdri)) as T;
                    break;
                case "audio":
                    asset = (await this.loadAudio(fullUrl, config.audio)) as T;
                    break;
                case "spritesheet":
                    if (!config.spritesheet) {
                        throw new Error("Spritesheet configuration is required for spritesheet type");
                    }
                    asset = (await this.loadSpritesheet(fullUrl, config.spritesheet)) as T;
                    break;
                default:
                    throw new Error(`Unsupported asset type: ${assetType}`);
            }

            // Estimate size (rough approximation)
            size = this.estimateAssetSize(asset, assetType);

            // Cache the asset
            this.cacheAsset(fullUrl, asset, assetType, size);

            const loadTime = performance.now() - startTime;

            return {
                asset,
                type: assetType,
                url: fullUrl,
                loadTime,
                size,
                timestamp: Date.now(),
            };
        } catch (error) {
            throw new Error(
                `Failed to load asset ${fullUrl}: ${error instanceof Error ? error.message : "Unknown error"}`
            );
        }
    }

    /**
     * Load texture asset
     * @private
     */
    private async loadTexture(
        url: string,
        config?: TextureConfig
    ): Promise<THREE.Texture> {
        const loader = this.loaders.get("texture") as THREE.TextureLoader;
        const textureConfig = { ...this.config.defaultTextureConfig, ...config };

        return new Promise((resolve, reject) => {
            loader.load(
                url,
                (texture) => {
                    // Apply configuration
                    if (textureConfig.mapping) texture.mapping = textureConfig.mapping;
                    if (textureConfig.wrapS) texture.wrapS = textureConfig.wrapS;
                    if (textureConfig.wrapT) texture.wrapT = textureConfig.wrapT;
                    if (textureConfig.magFilter)
                        texture.magFilter = textureConfig.magFilter;
                    if (textureConfig.minFilter)
                        texture.minFilter = textureConfig.minFilter;
                    if (textureConfig.generateMipmaps !== undefined)
                        texture.generateMipmaps = textureConfig.generateMipmaps;
                    if (textureConfig.anisotropy)
                        texture.anisotropy = textureConfig.anisotropy;

                    resolve(texture);
                },
                undefined,
                reject
            );
        });
    }

    /**
     * Load model asset
     * @private
     */
    private async loadModel(
        url: string,
        config?: ModelConfig
    ): Promise<THREE.Group> {
        const loader = this.loaders.get("model") as GLTFLoader;
        const modelConfig = { ...this.config.defaultModelConfig, ...config };

        return new Promise((resolve, reject) => {
            loader.load(
                url,
                (gltf) => {
                    const model = gltf.scene;

                    // Attach animations to the model for later access
                    // GLTF/GLB files store animations separately from the scene
                    (model as any).animations = gltf.animations;
                    (model as any).gltfData = gltf; // Store full GLTF data for advanced use cases

                    // Apply configuration
                    if (modelConfig.scale) model.scale.setScalar(modelConfig.scale);
                    if (modelConfig.position) model.position.copy(modelConfig.position);
                    if (modelConfig.rotation) model.rotation.copy(modelConfig.rotation);

                    // Configure shadows
                    model.traverse((child) => {
                        if (child instanceof THREE.Mesh) {
                            child.castShadow = modelConfig.castShadow ?? true;
                            child.receiveShadow = modelConfig.receiveShadow ?? true;
                        }
                    });

                    resolve(model);
                },
                undefined,
                reject
            );
        });
    }

    /**
     * Load HDRI asset
     * @private
     */
    private async loadHDRI(
        url: string,
        config?: HDRIConfig
    ): Promise<THREE.Texture> {
        const loader = this.loaders.get("hdri") as RGBELoader;
        const hdriConfig = { ...this.config.defaultHDRIConfig, ...config };

        return new Promise((resolve, reject) => {
            loader.load(
                url,
                (texture) => {
                    texture.mapping = THREE.EquirectangularReflectionMapping;

                    // Apply HDRI configuration if provided
                    if (hdriConfig.intensity !== undefined) {
                        texture.colorSpace = THREE.SRGBColorSpace;
                    }

                    resolve(texture);
                },
                undefined,
                reject
            );
        });
    }

    /**
     * Load audio asset
     * @private
     */
    private async loadAudio(
        url: string,
        config?: AudioConfig
    ): Promise<AudioBuffer> {
        const audioConfig = { ...this.config.defaultAudioConfig, ...config };

        return new Promise(async (resolve, reject) => {
            try {
                const response = await fetch(url);
                const arrayBuffer = await response.arrayBuffer();
                const audioContext = new (window.AudioContext ||
                    (window as any).webkitAudioContext)();
                const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

                // Log audio configuration for debugging
                console.log(`Loaded audio with config:`, {
                    volume: audioConfig.volume ?? 1.0,
                    loop: audioConfig.loop ?? false,
                    autoplay: audioConfig.autoplay ?? false
                });

                resolve(audioBuffer);
            } catch (error) {
                reject(error);
            }
        });
    }

    /**
     * Load spritesheet and split into individual textures
     * @private
     */
    private async loadSpritesheet(
        url: string,
        config: SpritesheetConfig
    ): Promise<THREE.Texture[]> {
        const loader = this.loaders.get("texture") as THREE.TextureLoader;

        return new Promise((resolve, reject) => {
            loader.load(
                url,
                (texture) => {
                    const image = texture.image;
                    const imageWidth = image.width;
                    const imageHeight = image.height;

                    // Calculate frame dimensions
                    const frameWidth = config.frameWidth || Math.floor(imageWidth / config.columns);
                    const frameHeight = config.frameHeight || Math.floor(imageHeight / config.rows);

                    // Calculate total frames
                    const totalFrames = config.frameCount || (config.columns * config.rows);

                    // Create canvas for extracting frames
                    const canvas = document.createElement('canvas');
                    canvas.width = frameWidth;
                    canvas.height = frameHeight;
                    const ctx = canvas.getContext('2d')!;

                    const textures: THREE.Texture[] = [];

                    // Extract each frame
                    for (let i = 0; i < totalFrames; i++) {
                        const row = Math.floor(i / config.columns);
                        const col = i % config.columns;

                        // Clear canvas
                        ctx.clearRect(0, 0, frameWidth, frameHeight);

                        // Draw the specific frame
                        ctx.drawImage(
                            image,
                            col * frameWidth,  // source x
                            row * frameHeight, // source y
                            frameWidth,        // source width
                            frameHeight,       // source height
                            0,                 // dest x
                            0,                 // dest y
                            frameWidth,        // dest width
                            frameHeight        // dest height
                        );

                        // Create texture from canvas
                        const frameTexture = new THREE.CanvasTexture(canvas.cloneNode(true) as HTMLCanvasElement);

                        // Apply texture configuration if provided
                        if (config.textureConfig) {
                            if (config.textureConfig.wrapS !== undefined) {
                                frameTexture.wrapS = config.textureConfig.wrapS;
                            }
                            if (config.textureConfig.wrapT !== undefined) {
                                frameTexture.wrapT = config.textureConfig.wrapT;
                            }
                            if (config.textureConfig.magFilter !== undefined) {
                                frameTexture.magFilter = config.textureConfig.magFilter;
                            }
                            if (config.textureConfig.minFilter !== undefined) {
                                frameTexture.minFilter = config.textureConfig.minFilter;
                            }
                            if (config.textureConfig.generateMipmaps !== undefined) {
                                frameTexture.generateMipmaps = config.textureConfig.generateMipmaps;
                            }
                            if (config.textureConfig.anisotropy !== undefined) {
                                frameTexture.anisotropy = config.textureConfig.anisotropy;
                            }
                        }

                        frameTexture.needsUpdate = true;
                        textures.push(frameTexture);
                    }

                    resolve(textures);
                },
                undefined,
                reject
            );
        });
    }

    /**
     * Detect asset type from URL or config
     * @private
     */
    private detectAssetType(config: AssetConfig): AssetType {
        if (config.type && config.type !== "auto") {
            return config.type;
        }

        const url = config.url.toLowerCase();
        const extension = url.split(".").pop();

        switch (extension) {
            case "hdr":
            case "exr":
                return "hdri";
            case "glb":
            case "gltf":
                return "model";
            case "mp3":
            case "wav":
            case "ogg":
            case "m4a":
                return "audio";
            case "jpg":
            case "jpeg":
            case "png":
                return "texture";
            default:
                return "texture"; // Default fallback
        }
    }

    /**
     * Get full URL with base URL
     * @private
     */
    private getFullUrl(url: string): string {
        // Use as-is if already absolute (CDN, data URI, etc.)
        if (url.startsWith("http://") || url.startsWith("https://") || url.startsWith("data:") || url.includes("://")) {
            return url;
        }
        return this.config.baseUrl ? `${this.config.baseUrl}/${url}` : url;
    }

    /**
     * Cache an asset
     * @private
     */
    private cacheAsset(
        url: string,
        asset: any,
        type: AssetType,
        size: number
    ): void {
        this.cache.set(url, {
            asset,
            type,
            url,
            timestamp: Date.now(),
            size,
            accessCount: 0,
            lastAccess: Date.now(),
        });
    }

    /**
     * Estimate asset size
     * @private
     */
    private estimateAssetSize(asset: any, type: AssetType): number {
        // Try to get actual size from asset if available
        if (asset && typeof asset === 'object') {
            // For textures, calculate based on dimensions
            if (type === "texture" && asset.image) {
                const width = asset.image.width || 512;
                const height = asset.image.height || 512;
                return width * height * 4; // RGBA bytes
            }
            // For audio buffers, calculate from buffer data
            if (type === "audio" && asset.length && asset.numberOfChannels) {
                return asset.length * asset.numberOfChannels * 4; // 32-bit float
            }
            // For spritesheets (array of textures), sum up all texture sizes
            if (type === "spritesheet" && Array.isArray(asset)) {
                return asset.reduce((total, texture) => {
                    if (texture.image) {
                        const width = texture.image.width || 64;
                        const height = texture.image.height || 64;
                        return total + (width * height * 4);
                    }
                    return total + (64 * 64 * 4); // Default frame size
                }, 0);
            }
        }

        // Fallback to default estimates
        switch (type) {
            case "texture":
                return 1024 * 100; // 100KB estimate
            case "model":
                return 1024 * 500; // 500KB estimate
            case "audio":
                return 1024 * 200; // 200KB estimate
            case "hdri":
                return 1024 * 1000; // 1MB estimate
            case "spritesheet":
                return 1024 * 250; // 250KB estimate for spritesheet
            default:
                return 1024 * 50; // 50KB default
        }
    }

    /**
     * Dispose of an asset
     * @private
     */
    private disposeAsset(asset: any, type: AssetType): void {
        if (type === "texture" && asset instanceof THREE.Texture) {
            asset.dispose();
        } else if (type === "spritesheet" && Array.isArray(asset)) {
            // Dispose all textures in the spritesheet
            asset.forEach((texture) => {
                if (texture instanceof THREE.Texture) {
                    texture.dispose();
                }
            });
        } else if (type === "model" && asset instanceof THREE.Group) {
            asset.traverse((child) => {
                if (child instanceof THREE.Mesh) {
                    if (child.geometry) child.geometry.dispose();
                    if (child.material) {
                        if (Array.isArray(child.material)) {
                            child.material.forEach((mat) => mat.dispose());
                        } else {
                            child.material.dispose();
                        }
                    }
                }
            });
        }
    }
}
